import { AssetType, DeviceFamily } from '@amzn/fire-tv-campaign-automation-service';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { createAssetHandler } from 'src/handlers/factories/assetHandlerFactory';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { AssetDetails, AssetState, AssetStatus } from 'src/types';
import {
  ASSET_CONFIG,
  getAssetInputsFromConfig,
  getInputTypeOptions,
  getValidationProps,
} from 'src/configs/assetConfig';
import { AssetConstants } from 'src/constants';
import styles from './AssetInputSection.module.css';
import { AssetInput } from './assets/AssetInput';
import { AssetTransformationHandler } from 'src/handlers/AssetTransformationHandler';
import { AssetValidationHandler } from 'src/handlers/validation/AssetValidationHandler';
import { logger } from 'src/logger';
import { useToast } from 'src/contexts/ToastContext';
import { useSelection } from 'src/contexts/SelectionContext';

/**
 * Asset statuses that allow user interaction and input
 */
const INTERACTIVE_ASSET_STATUSES = [
  AssetStatus.COMPLETED,
  AssetStatus.FAILED,
  AssetStatus.PENDING_INPUT,
  AssetStatus.BASIC_VALIDATION_FAILED,
  AssetStatus.BASIC_VALIDATION_SUCCESS,
];

/**
 * Main component for managing campaign asset inputs and configuration.
 *
 * Provides functionality for:
 * - Handling file uploads for images, logos and videos
 * - Managing form inputs like dropdowns and text areas
 * - Configuring inputs based on selected templates
 * - Managing loading states and validation
 * - Maintaining asset state and metadata
 * - Handling asset transformations and validations
 * - Managing abort controllers for cleanup
 * - Displaying toast notifications
 *
 * @component AssetInputSection
 * @returns {JSX.Element} A form section containing asset upload and configuration fields
 */
export const AssetInputSection: React.FC = () => {
  const controlPlaneClient = getControlPlaneClient();
  const { assetStates, updateAssetState } = useAssetContext();
  const { placement, deviceFamily } = useSelection();
  const abortControllerRef = useRef<AbortController | null>(null);
  const toast = useToast();
  const assetInputs = getAssetInputsFromConfig(deviceFamily, placement);
  const [assetDetails, setAssetDetails] = useState<Record<AssetType, AssetDetails>>(
    assetInputs.reduce(
      (acc, assetType) => {
        acc[assetType] = {};
        return acc;
      },
      {} as Record<AssetType, AssetDetails>,
    ),
  );

  /**
   * Creates an asset handler for managing asset state and uploads
   * @param {AssetType} assetType - The type of asset to create a handler for
   * @returns {Object} An asset handler instance with methods for processing uploads and managing state
   */
  const createAssetInputHandler = useCallback(
    (assetType: AssetType) => {
      const handleAssetDetailsChange = (details: AssetDetails) => {
        setAssetDetails((prevDetails) => ({
          ...prevDetails,
          [assetType]: {
            ...prevDetails[assetType],
            ...details,
          },
        }));
      };
      const handleAssetStateChange = (state: Partial<AssetState>) =>
        updateAssetState(assetType, state);
      const getTransformationVariant = () => assetStates[assetType].transformationVariant;

      return createAssetHandler(
        assetType,
        handleAssetStateChange,
        handleAssetDetailsChange,
        getTransformationVariant,
        controlPlaneClient,
        placement,
      );
    },
    [updateAssetState, controlPlaneClient, assetStates, placement],
  );

  /**
   * Creates a handler for managing asset transformations
   * @param {AssetType} assetType - The type of asset to create a transformation handler for
   * @returns {AssetTransformationHandler} Handler instance for managing asset transformations
   */
  const createAssetTransformationHandler = useCallback(
    (assetType: AssetType) => {
      const handleState = (state: Partial<AssetState>) => {
        updateAssetState(assetType, state);
      };
      return new AssetTransformationHandler({
        controlPlaneClient,
        toast,
        abortSignal: abortControllerRef.current?.signal,
        setState: handleState,
        placement,
      });
    },
    [updateAssetState, controlPlaneClient, toast, placement],
  );

  /**
   * Creates a handler for asset validation
   * @param {AssetType} assetType - The type of asset to create a validation handler for
   * @returns {AssetValidationHandler | undefined} Handler instance for validating assets, or undefined if validation props are missing
   */
  const createAssetValidationHandler = useCallback(
    (assetType: AssetType) => {
      const assetState = assetStates[assetType];
      const validationProps = getValidationProps(
        assetType,
        placement,
        assetState.transformationVariant,
      );

      if (!validationProps) {
        if (AssetConstants.NO_ASSET_VARIANTS.includes(assetState.transformationVariant)) {
          return; // Expected - no validation needed
        }

        // Unexpected missing validation - throw error
        throw new Error(
          `Missing validation props for asset type ${assetType} and template ${assetState.transformationVariant}`,
        );
      }

      return new AssetValidationHandler({
        validationProps,
        assetType,
        placement,
        getTransformationVariant: () => assetState.transformationVariant,
        setState: (state: Partial<AssetState>) => updateAssetState(assetType, state),
      });
    },
    [assetStates, updateAssetState, placement],
  );

  /** effect - runs on unmount or mount */
  useEffect(() => {
    logger.debug('Setting up the abort controller on mount');
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        logger.debug('Cleaning up polling on component unmount');
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  /** effect - resets asset details when placement changes */
  useEffect(() => {
    setAssetDetails(
      assetInputs.reduce(
        (acc, assetType) => {
          acc[assetType] = {};
          return acc;
        },
        {} as Record<AssetType, AssetDetails>,
      ),
    );
  }, [placement, deviceFamily]);

  /**
   * Renders an asset upload input component with configuration and handlers
   * @param {AssetType} assetType - Type of asset input to render (e.g. BACKGROUND_IMAGE, LOGO, VIDEO_TRAILER)
   * @returns {JSX.Element} The configured AssetInput component with:
   * - Display label and input options from asset config
   * - Asset input handler for managing uploads
   * - File type and size validation
   * - Error and status state management
   * - Transformation and validation handlers
   * - Asset type and details props
   */
  const renderAssetInput = useCallback(
    (assetType: AssetType) => {
      const config = ASSET_CONFIG[assetType];
      const assetState = assetStates[assetType];
      const inputOptions = getInputTypeOptions(assetType, deviceFamily, placement);
      const acceptedFileTypes = config.acceptedFileTypes[placement] ?? '';

      return (
        <AssetInput
          label={config.displayLabel}
          assetInputOptions={inputOptions}
          assetInputHandler={createAssetInputHandler(assetType)}
          acceptedFileTypes={acceptedFileTypes}
          maxFileSizeInBytes={config.globalMaxFileSizeInBytes}
          error={assetState.error}
          status={assetState.status}
          assetTransformationHandler={createAssetTransformationHandler(assetType)}
          assetValidationHandler={createAssetValidationHandler(assetType)}
          assetType={assetType}
          assetDetails={assetDetails[assetType] || {}}
          key={assetType}
          setState={(state: Partial<AssetState>) => {
            updateAssetState(assetType, state);
          }}
          disabled={!INTERACTIVE_ASSET_STATUSES.includes(assetState.status)}
        />
      );
    },
    [
      assetStates,
      assetDetails,
      createAssetTransformationHandler,
      createAssetValidationHandler,
      createAssetInputHandler,
      updateAssetState,
      deviceFamily,
      placement,
    ],
  );

  return (
    <div className={styles.container} data-testid="asset-input-section">
      {assetInputs.map((assetType) => renderAssetInput(assetType))}
    </div>
  );
};
